export enum MedicineItemType {
  MEDICINE = "medicine",
  SUPPLEMENT = "supplement",
}

export enum MedicineTimingRuleType {
  FIXED_SLOTS = "FIXED_SLOTS",
  FIXED_INTERVAL = "FIXED_INTERVAL",
  CONDITIONAL = "CONDITIONAL",
}

export enum MedicineStatus {
  ACTIVE = "ACTIVE",
  STOPPED = "STOPPED",
}

export enum MedicineSource {
  PRESCRIPTION = "PRESCRIPTION",
  MANUAL = "MANUAL",
}

export enum FoodContext {
  BEFORE_FOOD = "BEFORE_FOOD",
  AFTER_FOOD = "AFTER_FOOD",
  WITH_FOOD = "WITH_FOOD",
}

export enum DayFrequency {
  ONCE = "ONCE",
  TWICE = "TWICE",
  THRICE = "THRICE",
  FOUR_TIMES = "FOUR_TIMES",
}

export interface PillboxItem {
  id: string;
  itemType: MedicineItemType;
  memberId: string;
  name: string;
  dose?: string;
  frequency?: string;
  dayFrequency?: string;
  timingRuleType: MedicineTimingRuleType;
  slots?: string[];
  recurrenceIntervalDays?: number;
  selectedWeekdays?: number[];
  intervalHours?: number;
  intervalStartTime?: string;
  route?: string;
  notes?: string;
  reasonForTaking?: string;
  foodContext?: FoodContext;
  doctorName?: string;
  dateOfVisit?: string;
  startDate: string;
  endDate?: string;
  status: MedicineStatus;
  stoppedAt?: string;
  needsRefill: boolean;
  hasOverlap: boolean;
  source?: MedicineSource;
  documentId?: string;
  prescriptionId?: string;
}

// Payload for manual add (POST /pillbox/:memberId/:itemType) — R4 typed mode.
export interface CreateMedicineItemPayload {
  name: string;
  dose?: string;
  frequency?: string;
  dayFrequency?: DayFrequency;
  timingRuleType?: MedicineTimingRuleType;
  slots?: string[];
  recurrenceIntervalDays?: number;
  selectedWeekdays?: number[];
  route?: string;
  notes?: string;
  reasonForTaking?: string;
  foodContext?: FoodContext;
  doctorName?: string;
  startDate?: string;
  endDate?: string;
}

export interface DoseEvent {
  id: string;
  memberId: string;
  itemType: MedicineItemType;
  itemId: string;
  date: string;
  time: string;
  name: string;
  dose?: string;
  notes?: string;
  route?: string;
}

export interface ConditionalEntry {
  id: string;
  itemType: MedicineItemType;
  name: string;
  dose?: string;
  notes?: string;
}

export interface ScheduleDay {
  date: string; // YYYY-MM-DD
  doses: DoseEvent[];
  conditional: ConditionalEntry[];
}

export type DaytimeSlot = "morning" | "afternoon" | "evening" | "night";

export enum PendingChangeType {
  ADD = "ADD",
  DOSE_CHANGE = "DOSE_CHANGE",
  DISCONTINUE = "DISCONTINUE",
}

export enum PendingChangeStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

// A proposed ledger change surfaced from an OCR'd prescription, awaiting
// family review (R2/R8) — see PillboxPendingChange on the backend.
export interface PillboxPendingChange {
  id: string;
  memberId: string;
  changeType: PendingChangeType;
  itemType: MedicineItemType;
  targetItemId?: string;
  proposedFields: Record<string, unknown>;
  previousFields?: Record<string, unknown>;
  sourcePrescriptionId: string;
  sourceDocumentId: string;
  doctorName?: string;
  dateOfVisit?: string;
  status: PendingChangeStatus;
}

// AI-generated "About this medicine" blurb (R-Pillbox: generated + cached
// server-side by medicine name, see api MedicineInfoService).
export interface MedicineInfo {
  title: string;
  body: string;
}

// One start/stop cycle in a medicine's lineage (R18 — Resume creates a new
// entity per course). Most-recent-first; stopDate is null while ongoing.
export interface PillboxHistoryEntry {
  startDate: string;
  stopDate: string | null;
}
